import classNames from 'classnames';
import * as _ from 'lodash';
import { ChangeEvent, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { IMPROVER_ATTACHMENT_MIME_TYPE_TO_EXTENSION } from '@consts';
import { useBackgroundActivity } from '@hooks/useBackgroundActivity';
import { useMediaitemUpload } from '@hooks/useMediaitemUpload';
import ArrowUpIcon from '@icons/ArrowUpIcon';
import UploadIcon from '@icons/UploadIcon';
import { MediaitemUsageType } from '@models';
import { BackgroundJobActivityTarget, isUploadActivity } from '@models/backgroundActivities';
import { setupForDark } from '@theme';
import AttachmentCard from './AttachmentCard';
import Button from './Button';
import InputErrorMessage from './InputErrorMessage';

const getAllowedExtensionsText = (allowedFileTypes?: string[]): string => {
  if (!allowedFileTypes || allowedFileTypes.length === 0) return '';

  const mimeType = allowedFileTypes[0];
  return IMPROVER_ATTACHMENT_MIME_TYPE_TO_EXTENSION[mimeType];
};

const NOOP_ON_UPLOADING_CHANGE = (_: boolean) => {};

const MediaitemDragAndDropInput = ({
  target,
  placeholder,
  allowedFileTypes,
  maxFileSize,
  mediaitemUsageType,
  className,
  onUploaded,
  uploadedMediaitemId,
  uploadedFilename,
  onRemove,
  allowRemove = true,
  uploadConfirmed = false,
  onUploadingChange = NOOP_ON_UPLOADING_CHANGE,
  disabled = false
}: {
  target: BackgroundJobActivityTarget | null;
  placeholder?: string;
  allowedFileTypes?: string[];
  maxFileSize?: number;
  mediaitemUsageType: MediaitemUsageType;
  className?: string;
  onUploaded: (mediaitemId: number, originalFilename?: string, contentType?: string) => void;
  uploadedMediaitemId?: number;
  uploadedFilename?: string;
  onRemove?: () => void;
  allowRemove?: boolean;
  uploadConfirmed?: boolean;
  onUploadingChange?: (isUploading: boolean) => void;
  disabled?: boolean;
}) => {
  const { t } = useTranslation();

  const onUploadedRef = useRef(onUploaded);
  useEffect(() => {
    onUploadedRef.current = onUploaded;
  });

  const onUploadedAdapter = useMemo(
    () => (mediaitemId: number, originalFilename?: string, contentType?: string) => {
      onUploadedRef.current(mediaitemId, originalFilename, contentType);
    },
    []
  );

  const {
    inputId,
    fileInputRef,
    onChange,
    uploadProgress,
    error,
    acceptString,
    cancel,
    isUploading
  } = useMediaitemUpload({
    target,
    allowedFileTypes,
    maxFileSize,
    mediaitemUsageType,
    onUploaded: onUploadedAdapter
  });

  const activity = useBackgroundActivity(target);

  // Notify parent when uploading state changes
  const prevIsUploadingRef = useRef(isUploading);
  useEffect(() => {
    if (prevIsUploadingRef.current !== isUploading) {
      prevIsUploadingRef.current = isUploading;
      onUploadingChange(isUploading);
    }
  }, [isUploading, onUploadingChange]);

  // Drag and drop functionality
  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (disabled || isUploading) return;
    e.preventDefault();
    if (!e.dataTransfer.files || e.dataTransfer.files.length === 0) return;
    const file = e.dataTransfer.files[0];
    const syntheticTarget = { files: [file], value: '' };
    onChange({ target: syntheticTarget } as unknown as ChangeEvent<HTMLInputElement>);
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleRemove = () => {
    if (onRemove) {
      onRemove();
    }
  };

  const activityFilename = activity && isUploadActivity(activity) ? activity.payload.filename : '';

  if (isUploading && activityFilename) {
    return (
      <div className={className}>
        <AttachmentCard
          filename={activityFilename}
          progress={activity.progress ?? 0}
          onRemove={cancel}
          allowRemove={true}
        />
      </div>
    );
  }

  // File uploaded via props (from parent)
  if (uploadedMediaitemId && uploadedFilename) {
    return (
      <div className={className}>
        <AttachmentCard
          filename={uploadedFilename}
          progress={uploadConfirmed ? null : 100}
          onRemove={handleRemove}
          allowRemove={allowRemove}
        />
      </div>
    );
  }

  // Default state: show drag and drop interface
  return (
    <div className={className}>
      <input
        ref={fileInputRef}
        id={inputId}
        type='file'
        className='hidden'
        accept={acceptString}
        disabled={disabled || isUploading}
        onChange={onChange}
      />

      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        className={classNames(
          'w-full flex rounded-sm border-[2px] border-dashed overflow-hidden',
          setupForDark('border-dark bg-primary')
        )}
      >
        <div
          className={classNames(
            'w-[100px] flex flex-col items-center justify-center p-xs',
            setupForDark('bg-secondary')
          )}
        >
          <ArrowUpIcon size={18} />
          <div
            className={classNames('border rounded-xxs px-xxs P6M', setupForDark('text-secondary'))}
          >
            {getAllowedExtensionsText(allowedFileTypes)}
          </div>
        </div>

        <div
          className={classNames(
            'flex flex-col flex-1 items-center justify-center text-center p-sm',
            setupForDark('bg-primary')
          )}
        >
          <div className={classNames('P2R mb-xxs', setupForDark('text-primary'))}>
            {t('components.fileInput.dragHere')}
          </div>
          <div className={classNames('P2R opacity-70 mb-xs', setupForDark('text-secondary'))}>
            {t('components.fileInput.or')}
          </div>

          <Button labelFor={inputId} type='tertiary' size='small' onClick={_.noop} border={false}>
            <div className='P4M flex flex-row items-center justify-center gap-xs'>
              <UploadIcon size={16} />
              <span>{t('components.fileInput.upload')}</span>
            </div>
          </Button>

          {uploadProgress !== null && (
            <div className='P4M mt-xxs opacity-80'>
              {t('components.fileInput.progress', { progress: uploadProgress })}
            </div>
          )}
        </div>
      </div>

      {error && <InputErrorMessage error={error} />}
    </div>
  );
};

export default MediaitemDragAndDropInput;
